import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Mail, Send, Inbox, Trash2, Search,
  User, Loader2, X, MessageCircle, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import MembershipGuardModal, { canSendDM } from "../components/MembershipGuardModal";
import ChatSafetyActions from "../components/community/ChatSafetyActions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MessagesPage = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  
  const [activeTab, setActiveTab] = useState("inbox"); // inbox, sent
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [accessGuard, setAccessGuard] = useState(null);
  const [showGuardModal, setShowGuardModal] = useState(false);

  // 정회원 전용 가드
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setAccessGuard("auth");
      setShowGuardModal(true);
    } else if (!canSendDM(user)) {
      setAccessGuard("dm_upgrade");
      setShowGuardModal(true);
    } else {
      setAccessGuard(null);
      setShowGuardModal(false);
    }
  }, [authLoading, isAuthenticated, user]);

  useEffect(() => {
    if (!isAuthenticated && !user) {
      // Give it a moment for auth context to load
      const timer = setTimeout(() => {
        if (!isAuthenticated) {
          navigate("/");
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    if (isAuthenticated) {
      fetchMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab, navigate, user]);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const endpoint = activeTab === "inbox" ? "/api/messages/inbox" : "/api/messages/sent";
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await fetch(`${API_URL}/api/messages/${messageId}/read`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` },
      });
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      ));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("메시지를 삭제하시겠습니까?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/messages/${messageId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (response.ok) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error("Failed to delete message:", error);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    if (activeTab === "inbox" && !message.is_read) {
      handleMarkAsRead(message.id);
    }
  };

  if (!isAuthenticated && !user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  // 정회원 미만이면 차단 화면 노출
  if (accessGuard) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-28 pb-24 flex items-center justify-center px-4" data-testid="messages-access-blocked">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-rose-100 to-amber-100 mb-5">
            <Lock className="w-9 h-9 text-burgundy-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-2xl text-stone-900 mb-2">
            {accessGuard === "auth" ? "로그인이 필요한 페이지예요" : "쪽지는 정회원 전용이에요"}
          </h1>
          <p className="text-stone-500 text-[15px] leading-relaxed mb-6">
            메시지함은 따뜻한 인연을 시작한 정회원분들께 열려있는 공간이에요.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="text-burgundy-600 hover:text-burgundy-700 text-sm font-semibold underline underline-offset-4"
          >
            ← 뒤로 가기
          </button>
        </div>
        <MembershipGuardModal
          open={showGuardModal}
          reason={accessGuard}
          onClose={() => setShowGuardModal(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-stone-800">메시지함</h1>
          <Button 
            onClick={() => setShowComposeModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="compose-message-btn"
          >
            <Send className="w-4 h-4" />
            메시지 보내기
          </Button>
        </div>
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-48 flex-shrink-0">
            <nav className="bg-white rounded-2xl shadow-sm border border-stone-100 p-3 space-y-1">
              <button
                onClick={() => setActiveTab("inbox")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === "inbox" 
                    ? "bg-burgundy-50 text-burgundy-600" 
                    : "text-stone-600 hover:bg-stone-50"
                }`}
                data-testid="inbox-tab"
              >
                <Inbox className="w-5 h-5" />
                <span className="font-medium">받은 메시지</span>
              </button>
              <button
                onClick={() => setActiveTab("sent")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === "sent" 
                    ? "bg-burgundy-50 text-burgundy-600" 
                    : "text-stone-600 hover:bg-stone-50"
                }`}
                data-testid="sent-tab"
              >
                <Send className="w-5 h-5" />
                <span className="font-medium">보낸 메시지</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 flex gap-6">
            {/* Message List */}
            <div className="w-1/2 bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
              <div className="p-4 border-b border-stone-100">
                <h2 className="font-bold text-stone-800">
                  {activeTab === "inbox" ? "받은 메시지" : "보낸 메시지"}
                </h2>
              </div>
              
              {isLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-burgundy-500 mx-auto" />
                </div>
              ) : messages.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  <p>{activeTab === "inbox" ? "받은 메시지가 없습니다." : "보낸 메시지가 없습니다."}</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      onClick={() => handleSelectMessage(message)}
                      className={`p-4 cursor-pointer transition-colors ${
                        selectedMessage?.id === message.id 
                          ? "bg-burgundy-50" 
                          : activeTab === "inbox" && !message.is_read 
                            ? "bg-amber-50 hover:bg-amber-100" 
                            : "hover:bg-stone-50"
                      }`}
                      data-testid={`message-${message.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium ${
                          activeTab === "inbox" && !message.is_read ? "text-burgundy-600" : "text-stone-800"
                        }`}>
                          {activeTab === "inbox" ? message.sender_name : message.receiver_name}
                          {message.is_admin_message && (
                            <span className="ml-1.5 inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-burgundy-100 text-burgundy-700 align-middle">
                              운영팀
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-stone-400">
                          {new Date(message.created_at).toLocaleDateString("ko-KR")}
                        </span>
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-1">
                        {message.content}
                      </p>
                      {activeTab === "inbox" && !message.is_read && (
                        <span className="inline-block w-2 h-2 bg-burgundy-500 rounded-full mt-2"></span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message Detail */}
            <div className="w-1/2 bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
              {selectedMessage ? (
                <>
                  <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">
                        {activeTab === "inbox" ? "보낸 사람" : "받는 사람"}
                      </p>
                      <p className="font-bold text-stone-800">
                        {activeTab === "inbox" ? selectedMessage.sender_name : selectedMessage.receiver_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChatSafetyActions
                        otherId={activeTab === "inbox" ? selectedMessage.sender_id : selectedMessage.receiver_id}
                        otherName={activeTab === "inbox" ? selectedMessage.sender_name : selectedMessage.receiver_name}
                        size="sm"
                      />
                      {activeTab === "inbox" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowComposeModal(true);
                          }}
                          className="border-stone-300 text-stone-600"
                        >
                          답장
                        </Button>
                      )}
                      <button
                        onClick={() => handleDelete(selectedMessage.id)}
                        className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-xs text-stone-400 mb-4">
                      {new Date(selectedMessage.created_at).toLocaleString("ko-KR")}
                    </p>
                    <p className="text-stone-700 whitespace-pre-wrap leading-relaxed">
                      {selectedMessage.content}
                    </p>
                    {selectedMessage.images && selectedMessage.images.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {selectedMessage.images.map((url, idx) => {
                          const fullUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                          return (
                            <a
                              key={idx}
                              href={fullUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block rounded-lg overflow-hidden border border-stone-200 hover:opacity-90"
                            >
                              <img src={fullUrl} alt="" className="w-full h-40 object-cover" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {selectedMessage.is_admin_message && (
                      <div className="mt-4 px-3 py-2 bg-burgundy-50 border border-burgundy-200 rounded-lg text-xs text-burgundy-700">
                        🛡 New Coupling 운영팀에서 보낸 공식 메시지입니다.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-8 text-center text-stone-500 h-full flex items-center justify-center">
                  <div>
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                    <p>메시지를 선택하세요</p>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <ComposeModal 
          onClose={() => setShowComposeModal(false)} 
          onSent={() => {
            setShowComposeModal(false);
            if (activeTab === "sent") fetchMessages();
          }}
          replyTo={activeTab === "inbox" && selectedMessage ? selectedMessage.sender_id : null}
          replyToName={activeTab === "inbox" && selectedMessage ? selectedMessage.sender_name : null}
        />
      )}
    </div>
  );
};

// Compose Modal Component
const ComposeModal = ({ onClose, onSent, replyTo, replyToName }) => {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState(replyToName || "");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(replyTo ? { id: replyTo, username: replyToName } : null);
  const [content, setContent] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(user.username);
    setSearchResults([]);
  };

  const handleSend = async () => {
    if (!selectedUser) {
      setError("받는 사람을 선택하세요.");
      return;
    }
    if (!content.trim()) {
      setError("내용을 입력하세요.");
      return;
    }

    setIsSending(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          receiver_id: selectedUser.id,
          content: content.trim(),
        }),
      });

      if (response.ok) {
        onSent();
      } else {
        const data = await response.json();
        setError(data.detail || "메시지 전송에 실패했습니다.");
      }
    } catch (error) {
      setError("메시지 전송에 실패했습니다.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <h2 className="font-bold text-lg text-stone-800">메시지 보내기</h2>
          <button
            onClick={onClose}
            className="p-2 text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Recipient Search */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">받는 사람</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  handleSearch(e.target.value);
                  setSelectedUser(null);
                }}
                placeholder="닉네임으로 검색..."
                className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                data-testid="recipient-search"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 animate-spin" />
              )}
              
              {/* Search Results Dropdown */}
              {searchQuery.length >= 2 && !selectedUser && !isSearching && searchResults.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className="w-full px-4 py-3 text-left hover:bg-stone-50 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-stone-500" />
                      </div>
                      <span className="font-medium text-stone-800">{user.username}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* No Results */}
              {searchQuery.length >= 2 && !selectedUser && !isSearching && searchResults.length === 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-red-200 rounded-xl shadow-lg p-4 text-center">
                  <p className="text-red-500 text-sm">해당 닉네임의 회원을 찾을 수 없습니다.</p>
                </div>
              )}
            </div>
            
            {selectedUser && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-stone-500">선택됨:</span>
                <span className="px-3 py-1 bg-burgundy-100 text-burgundy-600 rounded-full text-sm font-medium flex items-center gap-1">
                  {selectedUser.username}
                  <button onClick={() => { setSelectedUser(null); setSearchQuery(""); }} className="ml-1 text-burgundy-400 hover:text-burgundy-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">내용</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="메시지 내용을 입력하세요..."
              className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 min-h-[150px] resize-none"
              data-testid="message-content"
            />
          </div>

          {/* Warning Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-amber-700 text-xs leading-relaxed">
              불쾌감을 주거나 불순한 내용의 메시지는 사전 통보 없이 삭제될 수 있습니다. 
              3회 이상 적발 시 <span className="font-semibold">강제 탈퇴</span> 조치될 수 있습니다.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-stone-300 text-stone-600"
          >
            취소
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !selectedUser || !content.trim()}
            className="btn-primary flex items-center gap-2"
            data-testid="send-message-btn"
          >
            {isSending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                보내기
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
